export const COMMISSION_TYPE = {
  NONE: "none",
  ADMIN_7CANTOS: "admin_7cantos",
  PARTNERSHIP: "partnership",
} as const;

export type CommissionType =
  (typeof COMMISSION_TYPE)[keyof typeof COMMISSION_TYPE];

export const COMMISSION_TYPE_VALUES: CommissionType[] = [
  COMMISSION_TYPE.NONE,
  COMMISSION_TYPE.ADMIN_7CANTOS,
  COMMISSION_TYPE.PARTNERSHIP,
];

export const COMMISSION_TYPE_OPTIONS: Array<{
  value: CommissionType;
  label: string;
}> = [
  {
    value: COMMISSION_TYPE.NONE,
    label: "Imóvel solto (sem ADM ou parceria)",
  },
  {
    value: COMMISSION_TYPE.ADMIN_7CANTOS,
    label: "Gerenciado pela 7Cantos (com ADM)",
  },
  {
    value: COMMISSION_TYPE.PARTNERSHIP,
    label: "Parceria com outro corretor/imobiliária",
  },
];

export const COMMISSION_PERCENTAGE_INVALID_MESSAGE =
  "Informe um percentual entre 0 e 100, com até duas casas decimais.";

export const COMMISSION_PARTNER_NAME_REQUIRED_MESSAGE =
  "Informe o nome do parceiro.";

export type PropertyCommissionForm = {
  commission_type: CommissionType;
  admin_percentage: string;
  first_rent_commission_7cantos_percentage: string;
  partner_name: string;
  partner_first_rent_commission_percentage: string;
};

export type PropertyCommissionErrors = Partial<
  Record<keyof PropertyCommissionForm, string>
>;

export type CommissionEstimate = {
  monthlyAdmin: number | null;
  firstRentCommission: number | null;
  firstMonthTotal: number | null;
};

const PERCENTAGE_PATTERN = /^(?:100(?:[.,]0{1,2})?|\d{1,2}(?:[.,]\d{1,2})?)$/;

export function isCommissionType(value: unknown): value is CommissionType {
  return (
    value === COMMISSION_TYPE.NONE ||
    value === COMMISSION_TYPE.ADMIN_7CANTOS ||
    value === COMMISSION_TYPE.PARTNERSHIP
  );
}

export function parseCommissionType(value: unknown): CommissionType {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const record = value as Record<string, unknown>;
    return parseCommissionType(
      record.value ?? record.name ?? record.slug ?? record.type
    );
  }
  if (typeof value !== "string") return COMMISSION_TYPE.NONE;

  const normalized = value.trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (isCommissionType(normalized)) return normalized;
  if (
    normalized === "admin" ||
    normalized === "adm" ||
    normalized === "admin7cantos" ||
    normalized === "adm_7cantos"
  ) {
    return COMMISSION_TYPE.ADMIN_7CANTOS;
  }
  if (
    normalized === "partner" ||
    normalized === "parceiro" ||
    normalized === "parceria"
  ) {
    return COMMISSION_TYPE.PARTNERSHIP;
  }
  return COMMISSION_TYPE.NONE;
}

export function emptyCommissionForm(
  type: CommissionType = COMMISSION_TYPE.NONE
): PropertyCommissionForm {
  return {
    commission_type: type,
    admin_percentage: "",
    first_rent_commission_7cantos_percentage: "",
    partner_name: "",
    partner_first_rent_commission_percentage: "",
  };
}

export function formatPercentageInput(value: unknown): string {
  if (value == null || value === "") return "";
  const numeric = Number(String(value).replace(",", "."));
  if (!Number.isFinite(numeric)) return "";
  return String(numeric);
}

function firstPresentValue(
  raw: Record<string, unknown>,
  keys: string[]
): unknown {
  const entries = Object.entries(raw);
  for (const key of keys) {
    const exact = raw[key];
    if (exact !== undefined && exact !== null && exact !== "") return exact;
    const found = entries.find(([entryKey]) => entryKey.toLowerCase() === key.toLowerCase());
    if (found && found[1] !== undefined && found[1] !== null && found[1] !== "") {
      return found[1];
    }
  }
  return undefined;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function commissionSourceRecords(
  raw: Record<string, unknown>
): Record<string, unknown>[] {
  const records = [raw];
  for (const key of ["property", "attributes", "data", "commission"]) {
    const nested = asRecord(raw[key]);
    if (nested) records.push(nested);
  }
  return records;
}

export function commissionFormFromApi(
  raw: Record<string, unknown>
): PropertyCommissionForm {
  const pick = (keys: string[]) => {
    for (const source of commissionSourceRecords(raw)) {
      const value = firstPresentValue(source, keys);
      if (value !== undefined) return value;
    }
    return undefined;
  };

  const admin_percentage = formatPercentageInput(
    pick(["admin_percentage", "adminPercentage", "adm_percentage"])
  );
  const first_rent_commission_7cantos_percentage = formatPercentageInput(
    pick([
      "first_rent_commission_7cantos_percentage",
      "firstRentCommission7cantosPercentage",
    ])
  );
  const partner_name = String(
    pick(["partner_name", "partnerName"]) ?? ""
  ).trim();
  const partner_first_rent_commission_percentage = formatPercentageInput(
    pick([
      "partner_first_rent_commission_percentage",
      "partnerFirstRentCommissionPercentage",
    ])
  );

  const rawType = pick(["commission_type", "commissionType"]);
  let commission_type = parseCommissionType(rawType);
  if (rawType === undefined && commission_type === COMMISSION_TYPE.NONE) {
    if (admin_percentage !== "" || first_rent_commission_7cantos_percentage !== "") {
      commission_type = COMMISSION_TYPE.ADMIN_7CANTOS;
    } else if (
      partner_name !== "" ||
      partner_first_rent_commission_percentage !== ""
    ) {
      commission_type = COMMISSION_TYPE.PARTNERSHIP;
    }
  }

  return {
    commission_type,
    admin_percentage,
    first_rent_commission_7cantos_percentage,
    partner_name,
    partner_first_rent_commission_percentage,
  };
}

export function applyCommissionFieldsToRecord(
  raw: Record<string, unknown>
): Record<string, unknown> {
  const form = commissionFormFromApi(raw);
  return {
    ...raw,
    ...form,
  };
}

export function listRowNeedsCommissionHydration(
  fields: PropertyCommissionForm
): boolean {
  return (
    fields.commission_type === COMMISSION_TYPE.NONE &&
    fields.admin_percentage === "" &&
    fields.first_rent_commission_7cantos_percentage === "" &&
    fields.partner_name === "" &&
    fields.partner_first_rent_commission_percentage === ""
  );
}

export function parseCommissionPercentage(value: unknown): number | null {
  if (value == null) return null;
  const normalized = String(value).trim().replace("%", "").replace(",", ".");
  if (normalized === "") return null;
  const numeric = Number(normalized);
  if (!Number.isFinite(numeric)) return null;
  return numeric;
}

export function validateCommissionPercentage(
  value: unknown,
  label: string
): string | null {
  const raw = String(value ?? "").trim().replace("%", "");
  if (raw === "") return `${label} é obrigatória.`;
  if (!PERCENTAGE_PATTERN.test(raw)) {
    return COMMISSION_PERCENTAGE_INVALID_MESSAGE;
  }
  const numeric = parseCommissionPercentage(raw);
  if (numeric == null || numeric < 0 || numeric > 100) {
    return COMMISSION_PERCENTAGE_INVALID_MESSAGE;
  }
  return null;
}

export function firstCommissionError(
  errors: PropertyCommissionErrors
): string {
  return Object.values(errors)[0] ?? "Informe os dados de comissão.";
}

export function validatePropertyCommission(
  fields: PropertyCommissionForm
): PropertyCommissionErrors {
  const type = parseCommissionType(fields.commission_type);
  if (type === COMMISSION_TYPE.ADMIN_7CANTOS) {
    return withoutEmptyErrors({
      admin_percentage: validateCommissionPercentage(
        fields.admin_percentage,
        "Porcentagem de ADM mensal"
      ),
      first_rent_commission_7cantos_percentage: validateCommissionPercentage(
        fields.first_rent_commission_7cantos_percentage,
        "Comissão 7Cantos no 1º aluguel"
      ),
    });
  }
  if (type === COMMISSION_TYPE.PARTNERSHIP) {
    const partnerName = fields.partner_name.trim();
    return withoutEmptyErrors({
      partner_name:
        partnerName === "" ? COMMISSION_PARTNER_NAME_REQUIRED_MESSAGE : null,
      partner_first_rent_commission_percentage: validateCommissionPercentage(
        fields.partner_first_rent_commission_percentage,
        "Comissão do parceiro no 1º aluguel"
      ),
    });
  }
  return {};
}

export function applyCommissionToPayload(
  payload: Record<string, unknown>,
  fields: PropertyCommissionForm
): Record<string, unknown> {
  const type = parseCommissionType(fields.commission_type);
  const next: Record<string, unknown> = {
    ...payload,
    commission_type: type,
  };

  if (type === COMMISSION_TYPE.ADMIN_7CANTOS) {
    next.admin_percentage = parseCommissionPercentage(fields.admin_percentage);
    next.first_rent_commission_7cantos_percentage = parseCommissionPercentage(
      fields.first_rent_commission_7cantos_percentage
    );
    next.partner_name = null;
    next.partner_first_rent_commission_percentage = null;
    return next;
  }

  if (type === COMMISSION_TYPE.PARTNERSHIP) {
    next.partner_name = fields.partner_name.trim() || null;
    next.partner_first_rent_commission_percentage = parseCommissionPercentage(
      fields.partner_first_rent_commission_percentage
    );
    next.admin_percentage = null;
    next.first_rent_commission_7cantos_percentage = null;
    return next;
  }

  next.admin_percentage = null;
  next.first_rent_commission_7cantos_percentage = null;
  next.partner_name = null;
  next.partner_first_rent_commission_percentage = null;
  return next;
}

export function formatCommissionSummary(
  fields: Pick<
    PropertyCommissionForm,
    | "commission_type"
    | "admin_percentage"
    | "first_rent_commission_7cantos_percentage"
    | "partner_name"
    | "partner_first_rent_commission_percentage"
  >
): string {
  const type = parseCommissionType(fields.commission_type);
  if (type === COMMISSION_TYPE.ADMIN_7CANTOS) {
    return `ADM: ${formatPercentLabel(fields.admin_percentage)} / 1º aluguel: ${formatPercentLabel(
      fields.first_rent_commission_7cantos_percentage
    )}`;
  }
  if (type === COMMISSION_TYPE.PARTNERSHIP) {
    const partner = fields.partner_name.trim() || "Parceiro";
    return `${partner} / 1º aluguel: ${formatPercentLabel(
      fields.partner_first_rent_commission_percentage
    )}`;
  }
  return "Imóvel solto (sem ADM ou parceria)";
}

export function parseCommissionEstimate(raw: unknown): CommissionEstimate | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const record = raw as Record<string, unknown>;
  const monthlyAdmin = readEstimateNumber(
    record.monthly_admin ?? record.admin_monthly ?? record.adm_monthly
  );
  const firstRentCommission = readEstimateNumber(
    record.first_rent_commission ??
      record.first_month_commission ??
      record.first_rent
  );
  const firstMonthTotal = readEstimateNumber(
    record.first_month_total ?? record.first_month
  );
  if (
    monthlyAdmin == null &&
    firstRentCommission == null &&
    firstMonthTotal == null
  ) {
    return null;
  }
  return { monthlyAdmin, firstRentCommission, firstMonthTotal };
}

export const COMMISSION_NULLABLE_PAYLOAD_KEYS = new Set([
  "admin_percentage",
  "first_rent_commission_7cantos_percentage",
  "partner_name",
  "partner_first_rent_commission_percentage",
]);

function formatPercentLabel(value: unknown): string {
  const numeric = parseCommissionPercentage(value);
  if (numeric == null) return "—";
  return `${numeric}%`;
}

function readEstimateNumber(value: unknown): number | null {
  if (value == null || value === "") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function withoutEmptyErrors(
  errors: Record<string, string | null>
): PropertyCommissionErrors {
  const result: PropertyCommissionErrors = {};
  for (const [key, message] of Object.entries(errors)) {
    if (message) result[key as keyof PropertyCommissionForm] = message;
  }
  return result;
}
